/**
 * Petstyle24 – Produktseite (sections/main-product.liquid; FINAL #40, #56; D+E-Plan §5.2–5.4).
 *
 * <ps-product>
 *   Variantenwechsel: Section Rendering mit der tatsächlich gerenderten section.id (data-section-id):
 *   GET <product.url>?variant=<id>&section_id=<section.id>. Ersetzt werden nur [data-ps-product-buy] und die
 *   Zeile Artikelnummer [data-ps-product-sku] in den Produktdetails (Gesamtplan Et. 7; DOMParser über
 *   Petstyle24.parseHTML, importNode, gleiche Herkunft, kein innerHTML). Preise, Verfügbarkeit, Button und SKU
 *   kommen damit von Shopify; im Browser wird nichts berechnet.
 *   Laufende Anfragen werden abgebrochen, die URL erst nach Erfolg auf ?variant= gesetzt. Schlägt das
 *   Laden fehl, lädt die Seite mit ?variant= neu (der Server rendert den richtigen Stand).
 *   Menge 1–10 (#56); seit Runde W3 (D-065) bis zum max-Attribut der Mengenwahl (Shopify rendert die kleinere Zahl aus
 *   Bestand und Höchstmenge, snippets/ps-stock.liquid), nie über 10. Die Grenze ist nur Bedienhilfe – den Bestand prüft
 *   Shopify beim Hinzufügen. Hinzufügen nur über Petstyle24.cart.add (Cart-Adapter); der Drawer öffnet dort.
 *   Fehler erscheinen am Button (role="alert"), ein Erfolg wird nie vorgetäuscht.
 *   Die Mengenleiste nutzt eigene data-ps-product-qty-*-Attribute: data-ps-qty-* gehört der
 *   Warenkorb-Delegation in ps-cart.js.
 *
 * <ps-product-gallery>
 *   Eine Bildliste für alle Breiten. < 768 px Swipe (Punkte über <ps-swipe>), ab 768 px Hauptbild +
 *   Thumbnails. Springt beim Variantenwechsel auf variant.featured_media – ohne Animation (#18).
 *
 * Kein eigener Scroll-Lock, kein Dialog, keine Warenkorb-Routen.
 */
(() => {
  'use strict';

  const PS = window.Petstyle24;
  if (!PS || customElements.get('ps-product')) return;

  const QTY_MIN = 1;
  const QTY_MAX = 10;
  const ADDED_MS = 2000;
  const strings = (PS.config && PS.config.strings) || {};

  const clampQuantity = (value, max = QTY_MAX) => {
    const number = Math.round(Number(value));
    if (!Number.isFinite(number)) return QTY_MIN;
    return Math.min(max, Math.max(QTY_MIN, number));
  };

  // Runde W3 (D-065 Nr. 3): Grenze aus dem max-Attribut des Mengenfelds (nach dem Größenwechsel das der neuen Größe);
  // fehlt es oder ist es ungültig, gilt 10
  const maxOf = (input) => {
    const number = Math.round(Number(input && input.max));
    if (!Number.isFinite(number) || number < QTY_MIN) return QTY_MAX;
    return Math.min(QTY_MAX, number);
  };

  const setLabel = (button, value) => {
    const label = button.querySelector('.button__label');
    if (label && value) label.textContent = value;
  };

  const setIcon = (button, visible) => {
    const icon = button.querySelector('.button__icon');
    if (icon) icon.hidden = !visible;
  };

  /* ---------- Galerie ---------- */
  class PsProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ps-gallery-track]');
      this.slides = this.track ? Array.from(this.track.children) : [];
      this.thumbs = Array.from(this.querySelectorAll('[data-ps-gallery-thumb]'));
      this.status = this.querySelector('[data-ps-gallery-status]');
      this.index = 0;
      if (!this.track || this.slides.length < 2) return;

      this.onClick = (event) => {
        const thumb = event.target instanceof Element ? event.target.closest('[data-ps-gallery-thumb]') : null;
        if (thumb) this.show(Number(thumb.dataset.psGalleryThumb));
      };
      this.onScroll = () => {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => this.sync());
      };
      this.addEventListener('click', this.onClick);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      // Breakpoint-Wechsel oder Größenänderung: das aktuelle Bild bleibt sichtbar
      this.resizeObserver = new ResizeObserver(() => this.show(this.index));
      this.resizeObserver.observe(this.track);

      const start = Number(this.dataset.activeIndex) || 0;
      if (start > 0) this.show(start);
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.onClick) this.removeEventListener('click', this.onClick);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    sync() {
      const width = this.track.clientWidth;
      if (!width) return;
      this.setIndex(Math.round(this.track.scrollLeft / width));
    }

    setIndex(value) {
      const index = Math.min(this.slides.length - 1, Math.max(0, value));
      this.index = index;
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (this.status && this.dataset.statusTemplate) {
        this.status.setAttribute('aria-label', this.dataset.statusTemplate.split('{i}').join(String(index + 1)));
      }
    }

    show(value) {
      if (!this.track || this.slides.length < 2) return;
      const index = Math.min(this.slides.length - 1, Math.max(0, Number(value) || 0));
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'auto' });
      this.setIndex(index);
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.show(index);
    }
  }

  /* ---------- Produktformular ---------- */
  class PsProduct extends HTMLElement {
    connectedCallback() {
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      this.syncQuantity();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('submit', this.onSubmit);
      if (this.controller) this.controller.abort();
    }

    region() {
      return this.querySelector('[data-ps-product-buy]');
    }

    quantityInput() {
      return this.querySelector('[data-ps-product-qty-input]');
    }

    quantity() {
      const input = this.quantityInput();
      return input ? clampQuantity(input.value, maxOf(input)) : QTY_MIN;
    }

    syncQuantity() {
      const input = this.quantityInput();
      if (!input) return;
      const max = maxOf(input);
      const value = clampQuantity(input.value, max);
      input.value = String(value);
      this.querySelectorAll('[data-ps-product-qty-step]').forEach((button) => {
        const step = Number(button.dataset.psProductQtyStep);
        const blocked = input.disabled || (step < 0 ? value <= QTY_MIN : value >= max);
        if (blocked) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    }

    onClick(event) {
      const step = event.target instanceof Element ? event.target.closest('[data-ps-product-qty-step]') : null;
      if (!step || !this.contains(step)) return;
      event.preventDefault();
      if (step.getAttribute('aria-disabled') === 'true') return;
      const input = this.quantityInput();
      if (!input || input.disabled) return;
      input.value = String(clampQuantity(Number(input.value) + Number(step.dataset.psProductQtyStep), maxOf(input)));
      this.syncQuantity();
    }

    onChange(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.matches('[data-ps-product-qty-input]')) {
        this.syncQuantity();
        return;
      }
      if (target.matches('[data-ps-size-selector] input[name="id"]') && target.checked) {
        this.switchVariant(target.value);
      }
    }

    async switchVariant(variantId) {
      const productUrl = PS.sameOriginUrl(this.dataset.productUrl);
      const sectionId = this.dataset.sectionId;
      if (!productUrl || !sectionId) return;

      const pageUrl = new URL(productUrl.href);
      pageUrl.searchParams.set('variant', variantId);
      const sectionUrl = new URL(pageUrl.href);
      sectionUrl.searchParams.set('section_id', sectionId);

      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;

      const region = this.region();
      const submit = region ? region.querySelector('[data-ps-product-submit]') : null;
      if (region) region.setAttribute('aria-busy', 'true');
      // Bis Shopify den Stand der Variante liefert, nichts hinzufügen
      if (submit) submit.setAttribute('aria-disabled', 'true');

      let html = null;
      try {
        const response = await fetch(sectionUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        html = await response.text();
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        window.location.assign(pageUrl.href);
        return;
      }
      if (controller !== this.controller) return;

      const doc = html ? PS.parseHTML(html) : null;
      const fresh = doc ? doc.querySelector('[data-ps-product-buy]') : null;
      if (!fresh || !region || !region.isConnected) {
        window.location.assign(pageUrl.href);
        return;
      }
      fresh.querySelectorAll('noscript').forEach((node) => node.replaceChildren());

      const active = document.activeElement;
      const focusKey = active && region.contains(active) && active.dataset ? active.dataset.psFocusKey : null;
      const quantity = this.quantity();

      const next = document.importNode(fresh, true);
      region.replaceWith(next);
      const sku = this.querySelector('[data-ps-product-sku]');
      const freshSku = doc.querySelector('[data-ps-product-sku]');
      if (sku && freshSku) sku.replaceWith(document.importNode(freshSku, true));
      else if (sku) sku.hidden = true; // Antwort ohne Zeile: keine veraltete Artikelnummer zeigen
      // Go-live-Runde (Wunsch 2, D-055 Nr. 6): Badge „Im Angebot“ neben der Marke folgt der gewählten Größe (aus der Section-Antwort)
      const sale = this.querySelector('[data-ps-product-sale]');
      const freshSale = doc.querySelector('[data-ps-product-sale]');
      const eyebrow = this.querySelector('.ps-product__eyebrow');
      if (sale && freshSale) sale.replaceWith(document.importNode(freshSale, true));
      else if (sale) sale.remove();
      else if (freshSale && eyebrow) eyebrow.append(document.importNode(freshSale, true));

      const input = this.quantityInput();
      if (input && !input.disabled) input.value = String(quantity);
      this.syncQuantity();
      if (focusKey) {
        const target = next.querySelector(`[data-ps-focus-key="${CSS.escape(focusKey)}"]`);
        if (target) target.focus({ preventScroll: true });
      }

      window.history.replaceState(window.history.state, '', pageUrl.pathname + pageUrl.search + pageUrl.hash);

      const gallery = this.querySelector('ps-product-gallery');
      if (gallery && typeof gallery.showMedia === 'function') gallery.showMedia(next.dataset.variantMediaId);
      // Runde W3 (D-065, Prüferbefund B-1): die Bestandszeile der neuen Größe mitsagen (Text aus der Section-Antwort)
      const stock = next.querySelector('.ps-stock');
      const stockText = stock ? stock.textContent.replace(/\s+/g, ' ').trim() : '';
      const message = [next.dataset.psAnnounce, stockText].filter(Boolean).join(', ');
      if (message) PS.announce(message);
    }

    async onSubmit(event) {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form || !form.matches('[data-ps-product-form]') || !PS.cart) return;
      event.preventDefault();

      const region = this.region();
      const button = region ? region.querySelector('[data-ps-product-submit]') : null;
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const field = region.querySelector('input[name="id"]:checked') || form.querySelector('input[type="hidden"][name="id"]');
      if (!field || field.disabled) return;

      const error = region.querySelector('[data-ps-product-error]');
      if (error) error.textContent = '';
      // Laden: aria-disabled statt disabled, damit der Tastaturfokus auf dem Button bleibt
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      setLabel(button, strings.adding);

      const result = await PS.cart.add(field.value, this.quantity(), { trigger: button });
      if (!button.isConnected) return;

      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      if (!result || !result.ok) {
        setLabel(button, strings.addToCart);
        if (error) error.textContent = (result && result.error) || strings.errorGeneric || '';
        return;
      }
      setIcon(button, true);
      setLabel(button, strings.added);
      window.setTimeout(() => {
        if (!button.isConnected) return;
        setIcon(button, false);
        setLabel(button, strings.addToCart);
      }, ADDED_MS);
    }
  }

  customElements.define('ps-product-gallery', PsProductGallery);
  customElements.define('ps-product', PsProduct);
})();
